import React, { useMemo } from 'react';
import { ArrowLeftCircle } from 'lucide-react';
import { getBlogBySlug, getRelatedBlogs } from '../data/blogs';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Blog } from '../data/blogs';
import ReactMarkdown from 'react-markdown';

interface BlogSEOProps {
  blog: Blog;
}

// Define theme color options
const themeColors = {
  blue: {
    gradient: "from-blue-900 to-indigo-800",
    accent: "text-blue-600",
    accentHover: "hover:text-blue-700",
    accentBg: "bg-blue-600",
    accentLight: "bg-blue-100",
    link: "text-blue-600"
  },
  green: {
    gradient: "from-emerald-800 to-teal-700",
    accent: "text-emerald-600",
    accentHover: "hover:text-emerald-700",
    accentBg: "bg-emerald-600",
    accentLight: "bg-emerald-100",
    link: "text-emerald-600"
  },
  purple: {
    gradient: "from-purple to-purple-800",
    accent: "text-purple",
    accentHover: "hover:text-purple",
    accentBg: "bg-purple",
    accentLight: "bg-purple",
    link: "text-purple"
  },
  amber: {
    gradient: "from-amber-800 to-orange-700",
    accent: "text-amber-600",
    accentHover: "hover:text-amber-700",
    accentBg: "bg-amber-600",
    accentLight: "bg-amber-100",
    link: "text-amber-600"
  },
  rose: {
    gradient: "from-rose-900 to-pink-800",
    accent: "text-rose-600",
    accentHover: "hover:text-rose-700",
    accentBg: "bg-rose-600",
    accentLight: "bg-rose-100",
    link: "text-rose-600"
  }
};




const BlogSEOHelmet: React.FC<BlogSEOProps> = ({ blog }) => {
  const { metaTitle, metaDescription, keywords } = blog.seo || {};

  const generateSchemaMarkup = () => {
    return {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": blog.title,
      "description": metaDescription || blog.excerpt,
      "author": blog.author.map(author => ({
        "@type": "Person",
        "name": author.name
      })),
      "publisher": {
        "@type": "Organization",
        "name": "Greh Pravesh Therapy",
        "logo": {
          "@type": "ImageObject",
          "url": "https://grehpraveshtherapy.com/logo.png"
        }
      },
      "datePublished": new Date().toISOString(),
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `https://grehpraveshtherapy.com/blog/${blog.slug}`
      }
    };
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{metaTitle || blog.title}</title>
      <meta name="description" content={metaDescription || blog.excerpt} />
      <link rel="canonical" href={`https://grehpraveshtherapy.com/blog/${blog.slug}`} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="article" />
      <meta property="og:title" content={blog.title} />
      <meta property="og:description" content={metaDescription || blog.excerpt} />
      <meta property="og:url" content={`https://grehpraveshtherapy.com/blog/${blog.slug}`} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={blog.title} />
      <meta name="twitter:description" content={metaDescription || blog.excerpt} />

      {/* Keywords */}
      {keywords && <meta name="keywords" content={keywords.join(', ')} />}

      {/* Schema Markup */}
      <script type="application/ld+json">
        {JSON.stringify(generateSchemaMarkup())}
      </script>
    </Helmet>
  );
};


const generateAuthorColorPalette = (authorId?: string) => {
  // Create a simple hash function to generate consistent colors
  const hashCode = (str: string) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash);
  };

  const colorSets = [
    {
      gradient: 'from-blue-100 to-indigo-200',
      accent: 'text-blue-600',
      accentHover: 'hover:text-blue-700',
      background: 'bg-blue-50',
      ring: 'ring-blue-200',
      buttonBg: 'bg-blue-500',
      buttonHover: 'hover:bg-blue-600'
    },
    {
      gradient: 'from-green-100 to-teal-200',
      accent: 'text-teal-600',
      accentHover: 'hover:text-teal-700',
      background: 'bg-teal-100',
      ring: 'ring-teal-200',
      buttonBg: 'bg-teal-500',
      buttonHover: 'hover:bg-teal-600'
    },
    {
      gradient: 'from-purple to-purple',
      accent: 'text-purple',
      accentHover: 'hover:text-purple',
      background: 'bg-purple',
      ring: 'ring-purple',
      buttonBg: 'bg-purple',
      buttonHover: 'hover:bg-purple'
    },
    {
      gradient: 'from-rose-100 to-pink-200',
      accent: 'text-rose-600',
      accentHover: 'hover:text-rose-700',
      background: 'bg-rose-50',
      ring: 'ring-rose-200',
      buttonBg: 'bg-rose-500',
      buttonHover: 'hover:bg-rose-600'
    },
    {
      gradient: 'from-amber-100 to-orange-200',
      accent: 'text-amber-600',
      accentHover: 'hover:text-amber-700',
      background: 'bg-amber-50',
      ring: 'ring-amber-200',
      buttonBg: 'bg-amber-500',
      buttonHover: 'hover:bg-amber-600'
    }
  ];

  if (!authorId) return colorSets[0];

  const paletteIndex = hashCode(authorId) % colorSets.length;
  return colorSets[paletteIndex];
}

const BlogTemplate: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const blog = slug ? getBlogBySlug(slug) : null;
  const relatedBlogs = blog ? getRelatedBlogs(blog, 3) : [];



  const authorColorPalette = blog?.author && blog?.author.length > 0 ? generateAuthorColorPalette(blog.author[0].id) : generateAuthorColorPalette();


  // Generate a consistent theme color based on the blog id or slug
  const theme = useMemo(() => {
    if (!blog) return themeColors.blue;
    // Use a hash of the blog ID or slug to consistently pick a theme color
    const themeKeys = Object.keys(themeColors);
    const index = blog.id.charCodeAt(0) % themeKeys.length;
    return themeColors[themeKeys[index] as keyof typeof themeColors];
  }, [blog]);

  // Handle case where blog is not found
  if (!blog) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 pt-20">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-gray-800 mb-4">Blog Not Found</h1>
          <Link to="/blog">
            <span className="text-blue-600 hover:underline inline-flex items-center gap-2">
              <ArrowLeftCircle /> Return to Blogs
            </span>
          </Link>
        </div>
      </div>
    );
  }




  return (
    <article className='min-h-screen pt-10'>
      <BlogSEOHelmet blog={blog} />

      {/* Hero Section */}
      <div className={`relative h-[120vh] md:h-[80vh] bg-gradient-to-r ${theme.gradient} overflow-hidden`}>
        <div className="absolute inset-0 bg-pattern opacity-10"></div>
        <div className="absolute inset-0 flex items-center">
          <div className="container mx-auto max-w-4xl px-4">
            <Link to="/blogs">
              <span className="inline-flex items-center text-white/80 hover:text-white mb-4 transition-colors duration-200">
                <ArrowLeftCircle className="mr-2" /> Back to Blogs
              </span>
            </Link>

            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white leading-tight">{blog.title}</h1>

            <div className="flex flex-wrap items-center gap-4 mb-6 text-white/90">
              {blog.author.map((author, index) => (
                <div key={author.id} className="flex items-center">
                  <img
                    src={author.avatar}
                    alt={author.name}
                    className='w-10 h-10 rounded-full mr-3 object-cover'
                    loading='lazy'
                  />
                  <div>
                    <span>{author.name}</span>
                  </div>
                  {index < blog.author.length - 1 && <span className="mx-2">•</span>}
                </div>
              ))}
            </div>


            <div className="flex flex-wrap gap-3 mb-6">
              {blog.tags.map((tag) => (
                <span
                  key={tag}
                  className="bg-white/20 px-3 py-1 rounded-full text-sm text-white hover:bg-white/30 transition-colors duration-200 cursor-pointer"
                >
                  {tag}
                </span>
              ))}
            </div>

            <p className="text-white/90 max-w-2xl text-lg">{blog.excerpt}</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="container mx-auto max-w-4xl px-4 py-12">
        <div className="bg-white rounded-xl  p-8 lg:p-12">


          <div className="prose prose-lg max-w-none">
            <ReactMarkdown>
              {blog.content}
            </ReactMarkdown>
          </div>


          {/* References */}
          {blog.references && blog.references.length > 0 && (
            <div className="mt-12 border-t pt-8">
              <h2 className="text-2xl font-semibold mb-6">References</h2>
              <ul className="space-y-3">
                {blog.references.map((reference, index) => (
                  <li key={index} className="text-gray-700">
                    {reference.url ? (
                      <a
                        href={reference.url}
                        className={`${theme.link} hover:underline`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {reference.text}
                      </a>
                    ) : (
                      reference.text
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-12 border-t pt-4">
            {blog.author.map((author, index) => (
              <div key={author.id} className={`${index > 0 ? 'mt-8' : ''} bg-gradient-to-br ${authorColorPalette.gradient} rounded-xl p-4 md:p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 group`}>
                <div className="text-center mb-6">
                  <h2 className={`text-2xl md:text-3xl font-bold ${authorColorPalette.accent} mb-2`}>
                    About {author.name}
                  </h2>
                  <div className={`h-1 w-20 ${authorColorPalette.buttonBg} mx-auto rounded-full mb-4 group-hover:w-32 transition-all duration-300`}></div>
                </div>

                <div className="flex flex-col md:flex-row items-center space-y-6 md:space-y-0 md:space-x-8">
                  <Link
                    to={`/therapist/${author.id}`}
                    className="flex-shrink-0 transform hover:scale-105 transition-transform duration-300"
                  >
                    <img
                      src={author.avatar}
                      alt={author.name}
                      className={`w-32 h-32 md:w-40 md:h-40 rounded-full object-cover border-4 border-white shadow-lg ring-4 ${authorColorPalette.ring}`}
                      loading='lazy'
                    />
                  </Link>

                  <div className="text-center md:text-left flex-grow space-y-4">
                    <Link
                      to={`/therapist/${author.id}`}
                      className={`block ${authorColorPalette.accentHover} transition-colors`}
                    >
                      <h3 className={`text-2xl font-semibold text-gray-800 ${authorColorPalette.accentHover} transition-colors`}>
                        {author.name}
                      </h3>
                    </Link>

                    {author.about && (
                      <p className="text-gray-600 text-base leading-relaxed max-w-2xl mx-auto md:mx-0">
                        {author.about}
                      </p>
                    )}

                    <div className="flex justify-center md:justify-start space-x-4 pt-2 text-xs md:text-md">
                      {/* LinkedIn Link */}
                      {author.linkedin && (
                        <a
                          href={author.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`text-white ${authorColorPalette.buttonBg} ${authorColorPalette.buttonHover} p-2 px-4 rounded-full transition-all duration-300 inline-flex items-center space-x-2`}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="w-5 h-5 mr-2"
                          >
                            <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.784 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                          </svg>
                          LinkedIn
                        </a>
                      )}

                      {/* Profile Button */}
                      <Link
                        to={`/therapist/${author.id}`}
                        className={`text-white ${authorColorPalette.buttonBg} ${authorColorPalette.buttonHover} p-2 px-4 rounded-full transition-all duration-300 inline-flex items-center space-x-2`}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="w-5 h-5 mr-2"
                        >
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                          <circle cx="12" cy="7" r="4"></circle>
                        </svg>
                        View Profile
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Related Articles */}
          {relatedBlogs.length > 0 && (
            <div className="mt-12">
              <h2 className="text-2xl font-semibold mb-6 text-white">Related Articles</h2>
              <div className="grid md:grid-cols-3 gap-6">
                {relatedBlogs.map((relatedBlog) => (
                  <Link
                    key={relatedBlog.id}
                    to={`/blog/${relatedBlog.slug}`}
                    className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                  >
                    <div className="p-6 flex flex-col">
                      <div className="flex items-center mb-3 overflow-hidden">
                        {relatedBlog.author.slice(0, 2).map((author, index) => (
                          <div key={author.id} className="flex items-center">
                            <img
                              src={author.avatar}
                              alt={author.name}
                              className="w-8 h-8 rounded-full mr-2 object-cover"
                            />
                            {index === 0 && (
                              <span className="text-gray-600 text-sm mr-2">
                                {author.name}
                                {relatedBlog.author.length > 1 && (
                                  <span className="text-gray-400">
                                    {relatedBlog.author.length > 2
                                      ? ` +${relatedBlog.author.length - 1} others`
                                      : ` +1 other`
                                    }
                                  </span>
                                )}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                      <h3 className="font-semibold text-lg mb-2 text-gray-800 line-clamp-2">{relatedBlog.title}</h3>
                      <p className="text-gray-600 text-sm line-clamp-3 mb-4">{relatedBlog.excerpt}</p>
                      <div className={`mt-auto flex items-center gap-2 ${theme.accent}`}>
                        <span>Read more</span>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                          <polyline points="12 5 19 12 12 19"></polyline>
                        </svg>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </article >
  );
};

export default BlogTemplate;
